/**
 * Full-regression run banner (task 11.2 / 13.19 surface).
 *
 * There is no automatic schedule any more — see the removal note in
 * `.github/workflows/nightly.yml` (it was the org's single biggest GitHub
 * Actions cost and ran the account's whole monthly minutes allowance out
 * mid-cycle, blocking every workflow in every repo). This is now a plain
 * on-demand trigger: press "Run now" to dispatch the same full regression that
 * used to run automatically, and see how the last one went.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, type NightlyState, type RunSummary } from '../api';

interface Props {
  /** Recent runs, used to find the most recent nightly. */
  history: RunSummary[];
}

export function NightlyBanner({ history }: Props) {
  const [nightly, setNightly] = useState<NightlyState | null>(null);
  const [triggering, setTriggering] = useState(false);
  // The server's refusal, shown verbatim. The UI does not decide whether the
  // nightly may start — it asks, and reports what it is told.
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  const refresh = useCallback(() => {
    api.nightly().then(setNightly).catch(() => setNightly(null));
  }, []);

  useEffect(refresh, [refresh]);

  const start = useCallback(async () => {
    // Guarded here as well as on the server. This one stops the second click of a
    // double-click ever becoming a request; the server's guard is what makes the
    // guarantee true for a crafted request or a second operator.
    if (triggering) return;
    setTriggering(true);
    setMessage(null);
    try {
      const { trigger } = await api.triggerNightly();
      setMessage({
        tone: 'ok',
        text: `Regression started. Follow it on GitHub — ${trigger.workflowUrl}`,
      });
    } catch (error) {
      setMessage({ tone: 'error', text: (error as Error).message });
    } finally {
      setTriggering(false);
      refresh();
    }
  }, [triggering, refresh]);

  // The most recent finished nightly. Nightlies are full regressions triggered by
  // CI rather than a person, which is what distinguishes them in history.
  const lastNightly = useMemo(
    () =>
      history.find(
        (r) =>
          r.preset === 'regression' &&
          /nightly|github|schedule/i.test(r.triggeredBy) &&
          ['passed', 'failed'].includes(r.status),
      ),
    [history],
  );

  return (
    <div className="surface p-4" data-testid="nightly-banner">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <span className="text-sm font-medium">Full regression</span>
        <span className="text-sm opacity-70">Runs on GitHub runners — the QA droplet stays free.</span>

        {/* Dispatches the actual workflow — this does not start a console run. */}
        <button
          type="button"
          className="btn btn-sm"
          data-testid="nightly-run-now"
          onClick={start}
          disabled={triggering || !nightly?.available || nightly?.inFlight}
          title={
            nightly?.unavailableReason ??
            (nightly?.inFlight
              ? 'A regression started from this console is already running.'
              : `Dispatches ${nightly?.workflow?.file ?? 'nightly.yml'} — full regression ` +
                `(${nightly?.selection?.scope ?? 'all'} against ` +
                `${nightly?.selection?.environment ?? 'qa'}), on GitHub runners.`)
          }
        >
          {triggering ? 'Starting…' : nightly?.inFlight ? 'Regression running' : 'Run now'}
        </button>

        {lastNightly && (
          <span className="ml-auto flex items-center gap-2 text-sm" data-testid="last-nightly">
            <span className="opacity-70">Last run:</span>
            <span className="pill pill-info">
              {lastNightly.status}
            </span>
            <span className="opacity-70">
              {lastNightly.counts.passed}/{lastNightly.counts.total} passed
              {lastNightly.counts.unstable > 0 && (
                <strong className="ml-1 text-warn">
                  · {lastNightly.counts.unstable} unstable
                </strong>
              )}
            </span>
          </span>
        )}
      </div>

      {/* The outcome of pressing Run now — the server's own words, success or
          refusal, never a paraphrase. */}
      {message && (
        <p
          className={`mt-2 text-sm ${message.tone === 'error' ? 'text-fail' : ''}`}
          data-testid="nightly-trigger-message"
        >
          {message.text}
        </p>
      )}

      {/* Why the button is disabled, when it is. A disabled control with no reason
          is what operators file tickets about. */}
      {nightly && !nightly.available && nightly.unavailableReason && (
        <p className="mt-2 text-sm opacity-70" data-testid="nightly-unavailable">
          {nightly.unavailableReason}
        </p>
      )}

      {/* A manually started nightly, distinguishable from a scheduled one — reading
          a trend across the two without knowing which is which is how a "flaky
          nightly" gets diagnosed that was really someone pressing the button. */}
      {nightly?.lastTrigger && (
        <p className="mt-2 text-sm opacity-70" data-testid="nightly-last-trigger">
          Last started manually by {nightly.lastTrigger.by} at{' '}
          {new Date(nightly.lastTrigger.at).toLocaleString()}.
        </p>
      )}

    </div>
  );
}
